import * as yup from 'yup'

import {
  requiredBool, requiredString
} from '../yup/schemas.js'

import { jobSchema } from './general.schema.js'
import { schoolSchema } from '../school/schema.js'
import { JOB_LOCATION_TYPE, VALIDATION } from '../constants.js'

export const createJobGroupSchema = jobSchema
  .omit(['id', 'groupId', 'status', 'jobsBoardType'])

const jobsBoardsSchema = yup.array().of(
  yup.object({
    id: schoolSchema.get('id'),
    entry: requiredBool,
    experienced: requiredBool
  }).test('at-least-one-property', VALIDATION.MESSAGES.NON_EMPTY_ARRAY, value => !!(value.entry || value.experienced))
    .objectRequired()
)

const createJobGroupBack = createJobGroupSchema.shape({
  jobsBoards: jobsBoardsSchema.notEmpty().required()
})

const createJobGroupFront = [
  createJobGroupSchema
    .pick([
      'jobLocationType',
      'position',
      'otherPositionName'
    ]),

  createJobGroupSchema
    .pick([
      'reportingLocation',
      'contactPersonName',
      'contactPersonPhoneNumber',
      'priorExperienceNeeded',
      'yearsOfExperienceNeeded'
    ])
    .omitNested([{ reportingLocation: ['endorsements'] }])
    .shape({
      schools: yup.array().of(schoolSchema.get('id')).notEmpty().required(),
      boardType: requiredString
    }),

  createJobGroupSchema
    .pickNested(['TWICCardRequired', 'minimumAge', { reportingLocation: ['endorsements'] }]),

  createJobGroupSchema.pick([
    'tuitionReimbursement',
    'signOnBonus',
    'signOnBonusMaxAmount',
    'signOnBonusPayablePeriod'
  ]),

  createJobGroupSchema.pickNested([
    'jobLocationType',
    { localJobOptions: ['fullTime', 'yearRoundWork'] },
    { regionalJobOptions: ['fullTime', 'yearRoundWork', 'payStructure', 'hourlyPayOptions', 'mileagePayOptions', 'percentageOfLoadPayOptions'] },
    { OTRJobOptions: ['fullTime', 'yearRoundWork', 'payStructure', 'hourlyPayOptions', 'mileagePayOptions', 'percentageOfLoadPayOptions'] },
    { teamDrivingJobOptions: ['fullTime', 'yearRoundWork', 'payStructure', 'hourlyPayOptions', 'mileagePayOptions', 'percentageOfLoadPayOptions'] }
  ]),

  createJobGroupSchema.pickNested([
    'jobLocationType',
    { localJobOptions: ['payStructure', 'hourlyPayOptions', 'mileagePayOptions', 'percentageOfLoadPayOptions'] },
    { regionalJobOptions: ['multiStopPay', 'layoverPay', 'breakdownPay', 'payPerDiem', 'dedicatedRoute', 'physicalRequirements', 'percentDropAndHook'] },
    { OTRJobOptions: ['multiStopPay', 'layoverPay', 'detentionPay', 'breakdownPay', 'payPerDiem', 'dedicatedRoute', 'physicalRequirements', 'percentDropAndHook', 'avarageTimeAtHome'] },
    { teamDrivingJobOptions: ['multiStopPay', 'layoverPay', 'detentionPay', 'breakdownPay', 'payPerDiem', 'dedicatedRoute', 'physicalRequirements', 'percentDropAndHook', 'avarageTimeAtHome'] }
  ]),

  createJobGroupSchema.pickNested([
    'jobLocationType',
    { localJobOptions: ['numberOfHoursPerWeek', 'mandatoryOvertime', 'overtimeAfter8HoursDay', 'overtimeAfter40HoursWeek'] },
    { regionalJobOptions: ['numberOfNightsAway', 'numberOfConsecutiveNightsAwayPerWeek', 'restPeriodWithSleeperBerths', 'companyProvidesMotels', 'drivingStates', 'drivingPercentage'] },
    { OTRJobOptions: ['restPeriodWithSleeperBerths', 'companyProvidesMotels', 'drivingStates', 'drivingPercentage'] },
    { teamDrivingJobOptions: ['restPeriodWithSleeperBerths', 'companyProvidesMotels', 'drivingStates', 'drivingPercentage'] }
  ]),

  createJobGroupSchema.pickNested([
    'jobLocationType',
    { localJobOptions: ['workDaysVary', 'workDays', 'workHoursVary', 'workHoursType'] }
  ]).shape({
    jobsBoards: jobsBoardsSchema.when('jobLocationType', {
      is: (value) => value === JOB_LOCATION_TYPE.REGIONAL || value === JOB_LOCATION_TYPE.OVER_THE_ROAD || value === JOB_LOCATION_TYPE.TEAM_DRIVING,
      then: jobsBoardsSchema.notEmpty().required()
    })
  }),

  createJobGroupSchema.pickNested([
    'jobLocationType',
    { localJobOptions: ['physicalRequirements', 'requiredLoadUnload', 'drivingPercentage'] }
  ]).shape({
    paymentMethod: yup.boolean().when('jobLocationType', {
      is: (value) => value === JOB_LOCATION_TYPE.REGIONAL || value === JOB_LOCATION_TYPE.OVER_THE_ROAD || value === JOB_LOCATION_TYPE.TEAM_DRIVING,
      then: (schema) => schema.isTrue(VALIDATION.MESSAGES.PAYMENT_METHOD).required()
    }),
    autoRenew: yup.mixed().when('jobLocationType', {
      is: (value) => value === JOB_LOCATION_TYPE.REGIONAL || value === JOB_LOCATION_TYPE.OVER_THE_ROAD || value === JOB_LOCATION_TYPE.TEAM_DRIVING,
      then: () => createJobGroupSchema.get('autoRenew')
    })
  }),

  createJobGroupSchema.pick(['jobLocationType']).shape({
    jobsBoards: jobsBoardsSchema.notEmpty().required()
  }),

  createJobGroupSchema.pick(['jobLocationType']).shape({
    paymentMethod: yup.boolean().isTrue(VALIDATION.MESSAGES.PAYMENT_METHOD).required(),
    autoRenew: createJobGroupSchema.get('autoRenew')
  })
]

export const createJobGroupSchemas = {
  front: createJobGroupFront,
  back: createJobGroupBack
}
